import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts";

// Authorization Code + PKCE with silent refresh, via the maintained oidc-client-ts
// library (replaces v1's hand-rolled auth.js). The library handles the code
// exchange, refresh-token rotation, expiry, and cross-tab sync.
const env = import.meta.env;

export const userManager = new UserManager({
  authority: env.VITE_COGNITO_AUTHORITY, // https://cognito-idp.<region>.amazonaws.com/<poolId>
  metadataUrl: env.VITE_COGNITO_METADATA_URL, // Hosted UI OIDC discovery doc
  client_id: env.VITE_COGNITO_CLIENT_ID,
  redirect_uri: `${env.VITE_APP_ORIGIN}/callback`,
  post_logout_redirect_uri: env.VITE_APP_ORIGIN,
  response_type: "code",
  scope: "openid email profile",
  automaticSilentRenew: true, // refresh before the 1h id token expires
  userStore: new WebStorageStateStore({ store: window.localStorage }),
});

export const login = () => userManager.signinRedirect();
export const completeLogin = () => userManager.signinRedirectCallback();
export const logout = () => userManager.signoutRedirect();

export async function getUser(): Promise<User | null> {
  const user = await userManager.getUser();
  if (user && !user.expired) return user;
  // No session yet → show login. Don't attempt a silent sign-in here: with no
  // refresh token it falls back to a hidden iframe that Cognito refuses to frame.
  if (!user) return null;
  try {
    return await userManager.signinSilent(); // expired session → refresh-token grant
  } catch {
    return null;
  }
}

/** Always returns a fresh id token (refreshing if needed) for an API call. */
export async function getIdToken(): Promise<string | null> {
  return (await getUser())?.id_token ?? null;
}

export async function emailOf(): Promise<string | null> {
  const user = await getUser();
  const email = user?.profile.email;
  return typeof email === "string" ? email.toLowerCase() : null;
}
